import React, { useCallback, useImperativeHandle, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { makeStyles } from '@material-ui/core'
import SydModal from '../../../commonDesign/SydModal'
import SydButton from '../../../commonDesign/Button'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import SydLabel from '../../../commonDesign/SydLabel'
import KdpInput from '../../../pages/admin/shared/KdpControls/KdpInput'
import { useModifyClientKeyDatapointValues } from '../../../../api/groups'
import { useAppContext } from '../../../../redux/slices/appContext'
import { KDP } from '../../../../policies/admin'
import { usePolicy } from '../../../../hooks/usePolicy'
const useStyles = makeStyles((theme) => ({
  kdpLabel: {
    display: 'flex',
    flexDirection: 'row',
    gap: '35px',
    alignItems: 'center',
    paddingBottom: '20px'
  },
  name: {
    fontSize: theme.typography.fontSizes.xl,
    fontWeight: theme.typography.weights.light
  },
  code: {
    fontSize: theme.typography.fontSizes.md,
    fontWeight: theme.typography.weights.light,
    color: theme.palette.text.secondary
  }
}))

export const KDPDialog = React.forwardRef(function KDPDialog (_, ref) {
  const [open, setOpen] = useState(false)
  const [kdp, setKdp] = useState(null)
  const hasAccess = usePolicy(KDP.editKDP)
  const { mutateAsync: modifyKdp, isLoading: isSaving } = useModifyClientKeyDatapointValues()
  const { clientId } = useAppContext()
  const classes = useStyles()
  const form = useForm({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      [kdp?.codeName]: kdp?.value || ''
    }
  })
  const close = useCallback(() => {
    if (hasAccess) {
      setOpen(false)
    }
  }, [setOpen, hasAccess])
  useImperativeHandle(ref, () => ({
    open: (kdp) => {
      setOpen(true)
      setKdp(kdp)
    },
    close: () => setOpen(false)
  }), [setOpen, setKdp])

  const onError = useCallback((errors) => {
    console.warn('errors', errors)
  }, [])
  const onSave = useCallback(async (data) => {
    if (!hasAccess) {
      return
    }
    const newDatapoint = {
      clientId,
      datapoints: [
        {
          datapointId: kdp.datapointId,
          value: data[kdp.codeName]
        }
      ]
    }
    // await the result and then close the modal
    const result = await modifyKdp(newDatapoint)
    if (result?.message === 'Ok') {
      close()
    } else {
      console.error('error modifying KDP', result)
    }
  }, [modifyKdp, kdp, clientId, hasAccess, close])
  useEffect(() => {
    if (kdp) {
      form.reset({
        [kdp.codeName]: kdp.value || ''
      })
    }
  }, [kdp, form])
  if (!kdp) {
    return null
  }
  return (
    <SydModal
      title='Edit Key Datapoint'
      open={open}
      onClose={close}
      size='medium'
      position='top'
      disablePortal={false}
    >
      <div>
        <div className={classes.kdpLabel}>
          <div className='flex flex-col gap-1'>
            <SydLabel label={kdp.displayName} className={classes.name} />
            <SydLabel label={kdp.codeName} variant='secondary' className={classes.code} />
          </div>
          <div className='flex flex-col gap-2'>
            <KdpInput name={kdp.codeName} control={form.control} datapoint={kdp} />
          </div>
        </div>
        <SydModalActions className='__actions'>
          <SydButton onClick={close} disabled={isSaving}>Cancel</SydButton>
          <SydButton variant='primary' onClick={form.handleSubmit(onSave, onError)} processing={isSaving}>Save KDP</SydButton>
        </SydModalActions>
      </div>
    </SydModal>
  )
})
